import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { Subscription } from "rxjs";
import { SelectItem } from "primeng/api";
import { UserRepository } from "../../../user/user.repository";
import { NotifyService } from "../../../notify/notify.service";
import { User } from "../../../user/user.model";
import { HttpParams } from "@angular/common/http";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { SelectedChipsComponent } from "../../chip/selected-chips.component";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";

export interface UserChangeEvent {
  originalEvent: MouseEvent;
  value: User[];
}

@Component({
  selector: "app-user-multiselect",
  templateUrl: "./user-multiselect.component.html",
  styleUrls: ["./user-multiselect.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, Select, Checkbox, FormsModule, SelectedChipsComponent],
})
export class UserMultiselectComponent implements OnInit, OnChanges {
  private notifyService = inject(NotifyService);
  private userRepository = inject(UserRepository);

  public readonly userChange = output<User[]>();
  @Input() public selectedUsers: User[] = [];
  public readonly placeholder = input("Users");
  public readonly team = input(null);

  public userList: SelectItem[] = [];
  private subscription = new Subscription();

  ngOnInit() {
    this.buildUserList();
  }

  ngOnChanges(changes: SimpleChanges) {
    // The selection can be set by the parent before the user list request
    // resolves; without a matching option the multiselect renders "null".
    if (changes.selectedUsers) {
      this.userList = this.withSelectedUsers(this.userList);
    }
  }

  public onChange(event: User[]) {
    this.userChange.emit(event);
  }

  public readonly chipLabel = (user: User): string => user.getFullName();
  public readonly chipKey = (user: User): number => user.getId();

  public removeUser(user: User) {
    this.selectedUsers = (this.selectedUsers ?? []).filter(
      (item) => item.getId() !== user.getId(),
    );
    this.onChange(this.selectedUsers);
  }

  public isSelected(user: User): boolean {
    return (this.selectedUsers ?? []).some(
      (item) => item.getId() === user.getId(),
    );
  }

  public allVisibleSelected(select: Select): boolean {
    const visible = this.visibleUsers(select);

    return visible.length > 0 && visible.every((user) => this.isSelected(user));
  }

  /**
   * Toggles the options the filter currently shows; users selected but hidden
   * by the filter keep their selection.
   */
  public toggleAll(select: Select) {
    const visible = this.visibleUsers(select);
    const current = this.selectedUsers ?? [];

    this.selectedUsers = this.allVisibleSelected(select)
      ? current.filter(
          (user) => !visible.some((item) => item.getId() === user.getId()),
        )
      : [...current, ...visible.filter((user) => !this.isSelected(user))];

    this.onChange(this.selectedUsers);
  }

  private visibleUsers(select: Select): User[] {
    return select
      .visibleOptions()
      .map((option: SelectItem) => option.value as User);
  }

  private toSelectItem(user: User): SelectItem {
    return {
      label: user.getFullName(),
      value: user,
    };
  }

  /**
   * Appends the selected users missing from the given items (not active ones,
   * or ones selected before the list request resolved). Always returns a new
   * array reference — p-select's `options` is a signal input and ignores
   * in-place mutation.
   */
  private withSelectedUsers(items: SelectItem[]): SelectItem[] {
    const merged = [...items];

    (this.selectedUsers ?? []).forEach((user) => {
      const found = merged.some((item) => item.value.getId() === user.getId());
      if (!found) {
        merged.push(this.toSelectItem(user));
      }
    });

    return merged;
  }

  private buildUserList() {
    let params = new HttpParams();
    const team = this.team();
    if (team) {
      params = params.append("criteria[team]", team);
    }

    this.subscription.add(
      this.userRepository.list(params).subscribe({
        next: (userList) => {
          this.userList = this.withSelectedUsers(
            userList.map((user) => this.toSelectItem(user)),
          );
        },
        error: (err) =>
          this.notifyService.toast(
            TOAST.ERROR,
            "Error",
            "User list has not been loaded",
          ),
      }),
    );
  }
}
